/**
 * Felt — oval-felt container. Owner: Phase-3 A3.1.
 *
 * Computes seat positions around an ellipse for either 4 or 6 seats. The
 * container exposes itself as the positioning context (CSS `position: relative`)
 * and renders an inset radial-gradient "felt" plus its children, which the
 * caller positions absolutely.
 *
 * The seat-layout convention (RULES §1.1): the local human is rendered at the
 * bottom (slot 0). Slot indices then proceed to the local player's right —
 * matching the engine's turn order `i -> (i + 1) mod n`. In screen
 * coordinates that walk is bottom → bottom-right → top-right → top → … and
 * back round. The mapping `seatIndex -> layoutSlot` is computed by the caller
 * (Table.tsx); this component is purely visual.
 *
 * Children are arbitrary — the caller composes Seat rings (absolutely
 * positioned via `--seat-slot-x/y` from `seatSlotStyle` below) plus a center
 * region for the trick. The container is responsive: it uses CSS to maintain
 * an oval aspect on common viewports and gracefully shrinks on narrow phones.
 *
 * Seat layout (RULES §1.1):
 *   4p: slot 0 bottom, slot 1 right, slot 2 top, slot 3 left.
 *   6p: slot 0 bottom, slot 1 bottom-right, slot 2 top-right,
 *       slot 3 top,    slot 4 top-left,    slot 5 bottom-left.
 *
 * Angles are measured from +X axis CCW; we want slot 0 to land at the bottom
 * (270deg in screen coords). Then slot k for n seats sits at:
 *
 *   angle = 270deg - k * (360deg / n)   (going clockwise around the table)
 *
 * That makes slot 1 fall to the right of slot 0 (PRD §5.5 FR-U1, RULES §1.1).
 */
import type { ReactNode } from "react";
import { TrumpReveal } from "./TrumpReveal.js";

export interface FeltProps {
  count: 4 | 6;
  children: ReactNode;
}

/**
 * Position on the ellipse for `layoutSlot` of `count` seats, as (x, y) where
 * 0..1 is the inset rect inside the felt. Slot 0 = bottom-center; further
 * slots proceed clockwise.
 */
export function seatPosition(layoutSlot: number, count: 4 | 6): { x: number; y: number } {
  // Parameterize by t = slot / count, walking clockwise on screen. We want:
  //   slot 0 -> bottom-centre  (x=0, y_screen=+1 i.e. ny near 1)
  //   slot 1 -> right          (x=+1, y=0)
  //   slot k=count/2 -> top    (x=0, y_screen=-1 i.e. ny near 0)
  // Using screen Y (down-positive): x = sin(2πt), y = cos(2πt).
  const t = (layoutSlot % count) / count;
  const theta = 2 * Math.PI * t; // 0 .. 2π
  const x = Math.sin(theta);
  const y = Math.cos(theta);

  // Map from unit circle [-1, 1] to inset normalized [0, 1] within the ellipse
  // with a slight padding so the seat rings aren't flush with the edge.
  const pad = 0.06;
  const nx = 0.5 + (1 - pad) * 0.5 * x;
  const ny = 0.5 + (1 - pad) * 0.5 * y;
  return { x: nx, y: ny };
}

/**
 * Inline style for a child positioned at a slot. Caller passes the slot and
 * count and gets back an absolute-position style that the Seat consumes.
 */
export function seatSlotStyle(layoutSlot: number, count: 4 | 6): React.CSSProperties {
  const { x, y } = seatPosition(layoutSlot, count);
  return {
    position: "absolute",
    left: `${(x * 100).toFixed(2)}%`,
    top: `${(y * 100).toFixed(2)}%`,
    transform: "translate(-50%, -50%)",
  };
}

export function Felt({ count, children }: FeltProps): JSX.Element {
  return (
    <div
      className={`felt felt-${count}p`}
      role="presentation"
      data-seats={count}
    >
      <div className="felt-surface" aria-hidden="true" />
      <div className="felt-content">{children}</div>
      {/* v1.4.1 Phase 22.3 — trump-call ceremony. Mounted at the `.felt`
       * level (sibling of `.felt-content`) so it overlays the entire
       * playing surface centered on the felt, above seat rings and trick
       * cards, beneath fixed overlays like Announce/RoundSummary. Renders
       * null when no ceremony is active. */}
      <TrumpReveal />
    </div>
  );
}
